import { useEffect, useRef, useState } from "react";

import { DecisionsResult } from "src/performance/types";
import { StrictDateRange } from "src/utils/timeWindow";

type UseAvailableVersionsOptions = {
  decisionsData: DecisionsResult | undefined;
  timeWindow: StrictDateRange;
  selectedVersions: string[] | undefined;
};

/**
 * Custom hook to manage available versions from decisions data with race condition prevention
 *
 * Prevents race condition: Ensures versions are only set once per time window,
 * even if multiple queries complete with selectedVersions === undefined
 *
 * @param decisionsData - The decisions query data containing version_id fields
 * @param timeWindow - The current time window, used to detect time range changes
 * @param selectedVersions - Current version filter selection (undefined = all versions)
 * @returns Array of available version IDs, sorted alphabetically
 */
export const useAvailableVersions = ({
  decisionsData,
  timeWindow,
  selectedVersions,
}: UseAvailableVersionsOptions): string[] => {
  const [allAvailableVersions, setAllAvailableVersions] = useState<string[]>(
    [],
  );
  const hasSetVersionsForTimeWindowRef = useRef(false);

  // Reset when time window changes
  useEffect(() => {
    hasSetVersionsForTimeWindowRef.current = false;
    setAllAvailableVersions([]);
  }, [timeWindow]);

  // Populate allAvailableVersions once per time window from initial query
  useEffect(() => {
    // Only run for "all versions" query
    if (selectedVersions !== undefined) {
      return;
    }
    // Populate versions once per time window
    if (hasSetVersionsForTimeWindowRef.current) {
      return;
    }
    // No data yet
    if (!decisionsData || decisionsData.length === 0) {
      return;
    }
    // Set versions and mark as set for this time window
    const versions = [
      ...new Set(decisionsData.map((d) => d.version_id)),
    ].sort();
    setAllAvailableVersions(versions);
    hasSetVersionsForTimeWindowRef.current = true;
  }, [decisionsData, selectedVersions]);

  return allAvailableVersions;
};
